import React, { useContext, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useAppConfiguration } from '../../shared/hook';
import { AlertContext } from '../../shared/app/AlertProvider';
import useApi from '../../shared/hook/useApi';
import { ModalWithConfirmation } from '../../shared/modal/ModalWithConfirmation';
import { MentorCardProps } from './interface';
import { selectUser } from '../../../store';
import MentorCardComponent from './mentor-card-component';

const MentorCard: FC<MentorCardProps> = ({ mentor, onMentor, loading }) => {
  const { t, i18n } = useTranslation();
  const { withUnlimitedCredits } = useAppConfiguration();
  const { showAlert } = useContext(AlertContext);
  const [requestCredit, setRequestCredit] = React.useState(false);
  const [requestCreditApi] = useApi('requestCreditApi');
  const { user } = useSelector(selectUser);

  const { id, name, details } = mentor;

  const recommendedLabel =
    mentor.isRecommended && t('v2.global.label_recommendedForYou');

  const submitCreditRequest = () => {
    requestCreditApi({
      offeringType: 'ONE_ON_ONE',
    }).then(() => {
      showAlert({
        severity: 'success',
        message: t('v2.global.snackbar_label_creditRequestSentOut'),
      });
      setRequestCredit(false);
    });
  };

  const onMentorOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    if (onMentor && user && user.creditBalance >= 3) {
      onMentor(mentor);
    } else {
      setRequestCredit(true);
    }
  };

  const getModal = () => {
    const type = withUnlimitedCredits
      ? 'unlimitedPricing_bookingTooManyOfferings'
      : 'creditSystem_requestCredits';

    const confirmBtnLabel = withUnlimitedCredits
      ? t(
          `v2.global.modal_unlimitedPricing_bookingTooManyOfferings_label_button`,
        )
      : t(`v2.global.modal_creditSystem_requestCredits_button_label`);

    return {
      title: t(`v2.global.modal_${type}_label_title`),
      text: t(`v2.global.modal_${type}_label_body`),
      confirmBtnLabel,
    };
  };
  // modal_creditSystem_requestCredits_button_label
  const { title, text, confirmBtnLabel } = getModal();

  const languageLabel = new Intl.DisplayNames([i18n.language], {
    type: 'language',
  });
  const languageList = details?.languages?.map(
    (lang) => languageLabel.of(lang) as string,
  );
  const focusTopicList = details?.focusTopics?.map((item) =>
    t(`v2.global.chip_label_topic_${item}`),
  );

  return (
    <>
      <MentorCardComponent
        id={id}
        height="100%"
        onClick={onMentorOpen}
        profileImage={details?.profileImage}
        name={name}
        education={details?.education}
        description={details?.description}
        languages={languageList}
        focusTopics={focusTopicList}
        recommendedLabel={recommendedLabel}
        loading={loading}
      />

      <ModalWithConfirmation
        onClose={() => setRequestCredit(false)}
        onCancel={() => setRequestCredit(false)}
        isOpen={requestCredit}
        onConfirm={submitCreditRequest}
        title={title}
        text={text}
        confirmBtnLabel={confirmBtnLabel}
        cancelBtnlabel={t('v2.global.button_label_cancel')}
        snackbar={<></>}
      />
    </>
  );
};

export default MentorCard;
export { default as MentorCardComponent } from './mentor-card-component';
